import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Maximize2, Loader2, MapPin, Clock, CheckCircle, Truck } from 'lucide-react';
import { apiRequest, formatDateTime } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function DriverAlertsWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      const result = await apiRequest('/driver/alerts?status=active');
      setData(result);
    } catch (error) {
      console.error('Failed to fetch alerts');
      setData({ alerts: [] });
    } finally {
      setLoading(false);
    }
  };

  const getAlertTypeColor = (type) => {
    switch (type) {
      case 'overstay': return 'bg-amber-100 text-amber-800';
      case 'delivery_issue': return 'bg-red-100 text-red-800';
      case 'no_gps': return 'bg-slate-100 text-slate-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getAlertTypeLabel = (type) => {
    switch (type) {
      case 'overstay': return 'Overstay';
      case 'delivery_issue': return 'Issue';
      case 'no_gps': return 'GPS Lost';
      default: return type;
    }
  };

  const activeAlerts = data?.alerts || [];

  return (
    <Card data-testid="driver-alerts-widget" className={activeAlerts.length > 0 ? 'border-amber-200' : ''}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className={`h-5 w-5 ${activeAlerts.length > 0 ? 'text-amber-500' : 'text-slate-400'}`} />
            <CardTitle className="text-lg">Driver Alerts</CardTitle>
            {activeAlerts.length > 0 && (
              <Badge className="bg-amber-100 text-amber-800">{activeAlerts.length}</Badge>
            )}
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/admin/driver-tracking')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : activeAlerts.length === 0 ? (
          <div className="text-center py-4 text-slate-400">
            <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-400" />
            <p className="text-sm text-green-600">No active alerts</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activeAlerts.slice(0, 3).map((alert, idx) => (
              <div 
                key={alert.id || idx} 
                className="p-2 bg-amber-50 border border-amber-200 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors"
                onClick={() => navigate('/admin/driver-tracking')}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-amber-600" />
                    <span className="font-medium text-sm">{alert.driver_name}</span>
                  </div>
                  <Badge className={getAlertTypeColor(alert.type)}>
                    {getAlertTypeLabel(alert.type)}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 text-xs text-amber-700">
                  <MapPin className="h-3 w-3" />
                  <span>{alert.store_name}</span>
                </div>
                <p className="text-xs text-amber-600 mt-1 line-clamp-1">{alert.message}</p>
              </div>
            ))}
            
            {activeAlerts.length > 3 && (
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full text-xs text-amber-600"
                onClick={() => navigate('/admin/driver-tracking')}
              >
                View all {activeAlerts.length} alerts
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
